import React, { useEffect, useMemo, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import MediaField from "../AdminShared/MediaField"
import GalleryField from "../AdminShared/GalleryField"
import { MediaDropzone } from "../Store/extensions/shared/shared"
import { urljoin } from "../Store/extensions/shared/useProductEditor"
import { ExclamationTriangleIcon, CheckIcon, PhotoIcon, PlayCircleIcon } from "@heroicons/react/24/outline"
import { showNotification } from "aldoo-ra/Notification"
import { useVendor } from "../AdminShared/VendorContext"
import { NewProduct, SaveProduct, SaveLocalization, UploadLocalizedImage, LoadRawProduct, ReviewProduct, ProductTypeSet } from "../AdminShared/api"
import { Field, TextInput, TextArea, errorMessage } from "../AdminShared/ui"
import { useContentTypes } from "../Store/extensions/shared/content-types"
import ProductTypeBrowser from "../AdminShared/product-types/ProductTypeBrowser"
import TagsEditor from "../AdminShared/product-types/TagsEditor"
import { useProductTypes, refreshProductTypes, profileIdOf, ROOT, nameOf } from "../AdminShared/product-types/useProductTypes"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { PRODUCTS } from "../Vendor/texts/bundles"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

// Bulgarian letters as they are written in Latin (the official streamlined
// system), so a title in Bulgarian still makes a readable id.
const LATIN = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m",
  н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts", ч: "ch", ш: "sh",
  щ: "sht", ъ: "a", ь: "y", ю: "yu", я: "ya",
}

const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[а-я]/g, (letter) => LATIN[letter] ?? "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

// What the server's refusals mean to the person creating the product.
const REFUSED = {
  already_exists: "This product already exists. Use another name.",
  invalid_pid: "The product id must be <name>.<company id>",
  // a name whose id would read as something else (book.<cid>, author.<cid>)
  reserved_slug: "This name cannot be a product id. Use another name.",
  unknown_content_type: "This product type is not available. Pick another one.",
}

/** The refusal in words: a known code, an older "already exists", or the request failing outright. */
const COULD_NOT_CREATE = "Could not create the product. Try again."
const refusalOf = (err) => {
  if (!err) return COULD_NOT_CREATE
  if (REFUSED[err]) return REFUSED[err]
  if (/already.?exists|duplicate|E11000/i.test(err)) return REFUSED.already_exists
  return err
}

// the quick way's steps, in order
const STEPS = ["type", "name", "gallery", "price", "review"]
const MAX_GALLERY = 8
// the language the quick way writes in: the store's
const LANGUAGE = "bg"

/** Plain text as the description's HTML: paragraphs at blank lines, line breaks kept, markup escaped. */
const htmlOf = (text) =>
  String(text || "")
    .trim()
    .split(/\n\s*\n/)
    .map((part) => part.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>"))
    .filter(Boolean)
    .map((part) => `<p>${part}</p>`)
    .join("")

/** A YouTube link, or "": what the product's video (its trailer) takes. */
const youtubeOf = (value) => {
  const text = String(value || "").trim()
  return /^(https?:\/\/)?(www\.|m\.)?(youtube\.com\/(watch\?v=|shorts\/|embed\/)|youtu\.be\/)[\w-]{6,}/i.test(text) ? (/^https?:/.test(text) ? text : `https://${text}`) : ""
}

/** "19,90" / "19.90" as cents, or NaN. */
const centsOf = (value) => {
  const number = Number(String(value || "").replace(",", ".").trim())
  return Number.isFinite(number) && number > 0 ? Math.round(number * 100) : NaN
}

/**
 * A new product, the quick way: a wizard of five steps — what it is (the product-type tree; a type not listed
 * is named and approved with the product's review), its name and description, the pictures of its gallery
 * (the first is the main one, square) and a video, its price (and, a physical one, its weight), and the
 * review it is sent for. The product is made when the name step is done (its id follows from the name and
 * the company id); each step after writes what it holds. Closed half way, the product stays as a draft.
 * "Open in the editor" leaves for the full product dialog (`onCreated(pid)`).
 *
 * `orgId`: the organization whose page the dialog was opened on — the publisher (products are made from an
 * organization's page, so it is not shown or picked here). `org`: that organization itself,
 * for a host with no VendorProvider above it (the merchant portal) — without
 * it the company id, and with it the pid, would be unknown.
 */
const FORM_ID = "quick-product-form"

// a field the step cannot go on without
const Required = () => <span className="ml-0.5 text-admin_error" aria-hidden>*</span>

// `onChanged`: the product made or written (a step saved, sent for review) — the host's list reads it again
export default function CreateProductDialog({ onClose, onCreated, onChanged, orgId: orgIdProp, org: orgProp }) {
  const t = usePortalTexts(PRODUCTS)
  const vendor = useVendor()
  const orgs = orgProp && !vendor.orgs.some((o) => o._id === orgProp._id) ? [orgProp, ...vendor.orgs] : vendor.orgs
  const orgId = orgIdProp ?? orgProp?._id ?? vendor.orgId
  const [publisher, setPublisher] = useState(orgId || orgs[0]?._id || "")
  const [step, setStep] = useState(0)
  const [productType, setProductType] = useState("")
  const [proposal, setProposal] = useState(null)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  // the vendor's own tags (the type's come with it)
  const [tags, setTags] = useState([])
  // the product once made (its pid): the steps after the name write into it
  const [created, setCreated] = useState("")
  const [mainImage, setMainImage] = useState("")
  const [gallery, setGallery] = useState([])
  const [video, setVideo] = useState("")
  // where the media library's relative paths are served from (the editor's mediaRoot)
  const [mediaRoot, setMediaRoot] = useState("")
  const [price, setPrice] = useState("")
  const [weight, setWeight] = useState("")
  // a physical product's units on hand: its page cannot sell it without (page-templates KIND_NEEDS)
  const [stock, setStock] = useState("")
  const [notes, setNotes] = useState("")
  const [busy, setBusy] = useState("")
  // why the last attempt was refused, shown until the step's fields change
  const [warning, setWarning] = useState("")
  const { bySlug } = useProductTypes()
  const contentTypes = useContentTypes()

  useEffect(() => {
    if (orgId) return setPublisher(orgId)
    if (!publisher && orgs.length) setPublisher(orgs[0]._id)
  }, [orgs, orgId])

  // the product made: where its pictures are served from
  useEffect(() => {
    if (!created || mediaRoot) return
    LoadRawProduct(created).then((res) => setMediaRoot(res?.mediaRoot || ""))
  }, [created, mediaRoot])

  const org = orgs.find((o) => o._id === publisher)
  const cid = org?.cid || slugify(org?.name)
  const slug = slugify(name)
  // new products get a bare <slug>.<cid> id (what the web urls carry)
  const pid = created || (slug && cid ? `${slug}.${cid}` : "")
  // the profile the picked type (or the group a named one goes under) gives the product; a type named at the
  // top (no group) takes the root's until the review places it
  const typeNode = productType ? bySlug.get(productType) : proposal ? bySlug.get(proposal.parentSlug) : null
  // the type's profile, or — a type proposed — its group's (the root's at the top)
  const contentTypeId = productType ? profileIdOf(bySlug, productType) : proposal ? profileIdOf(bySlug, proposal.parentSlug || ROOT) : ""
  const contentType = useMemo(() => contentTypes.find((type) => type.contentId === contentTypeId) || null, [contentTypes, contentTypeId])
  const physical = !!contentType?.isPhysical
  const cents = centsOf(price)

  const fail = (message) => {
    setWarning(message)
    return false
  }

  /** The product made (once) with its name and type, then its description written. */
  const saveName = async () => {
    if (!created) {
      const res = await NewProduct(pid, publisher, { name: name.trim(), productType: productType || undefined, typeProposal: proposal || undefined }).catch(() => null)
      if (!res) return fail(t(refusalOf("")))
      const err = errorMessage(res)
      if (err || !res?.product) return fail(t(refusalOf(err)))
      setCreated(pid)
      if (proposal) refreshProductTypes()
    } else {
      await SaveLocalization(`Product-name-${pid}`, { [LANGUAGE]: name.trim() })
    }
    // written in Bulgarian: its first language, so the editor opens on what was written
    await SaveProduct(pid, { languages: [LANGUAGE], ...(description.trim() ? { description: `Product-description-${pid}` } : {}), tags })
    if (description.trim()) await SaveLocalization(`Product-description-${pid}`, { [LANGUAGE]: htmlOf(description) })
    return true
  }

  /** Back on the type step with the product made: its type changed (its tabs stay those it was made with). */
  const saveType = async () => {
    if (!created) return true
    const res = await ProductTypeSet(pid, productType ? { slug: productType } : { proposal })
    const err = errorMessage(res)
    if (err) return fail(err)
    if (proposal) refreshProductTypes()
    return true
  }

  /** The gallery (after the main picture) and the video written. */
  const saveGallery = async () => {
    if (video.trim() && !youtubeOf(video)) return fail(t`Enter a YouTube link, or leave the video empty`)
    // the video is the product's main one (its trailer follows it, catalog lib/product-videos)
    await SaveProduct(pid, { screenshots: gallery, ...(youtubeOf(video) ? { videos: [{ url: youtubeOf(video), main: true }] } : {}) })
    return true
  }

  /** The price, and a physical product's weight (the delivery price is computed from it) and stock, written. */
  const savePrice = async () => {
    if (!Number.isFinite(cents)) return fail(t`Enter the price, e.g. 19.90`)
    const grams = parseInt(weight, 10)
    const units = parseInt(stock, 10)
    if (physical && !(grams > 0)) return fail(t`Enter the weight in grams: the delivery price is computed from it`)
    if (physical && !(units > 0)) return fail(t`Enter how many you have in stock`)
    const res = await SaveProduct(pid, { cost: `EUR_${cents}`, ...(physical ? { fields: `Product-fields-${pid}`, stock: units } : {}) })
    const err = errorMessage(res)
    if (err) return fail(err)
    if (physical) await SaveLocalization(`Product-fields-${pid}`, { bg: { weight: String(grams) }, en: { weight: String(grams) } })
    return true
  }

  const canNext = [!!(productType || proposal), !!(pid && publisher && name.trim()), !!mainImage, Number.isFinite(cents) && (!physical || (parseInt(weight, 10) > 0 && parseInt(stock, 10) > 0)), true][step]

  const next = async () => {
    if (!canNext || busy) return
    setBusy("next")
    setWarning("")
    try {
      const done = step === 0 ? await saveType() : step === 1 ? await saveName() : step === 2 ? await saveGallery() : step === 3 ? await savePrice() : true
      if (done) setStep((current) => Math.min(current + 1, STEPS.length - 1))
      // from the name on the product exists (a draft): the list behind the dialog shows it as it is now
      if (done && step >= 1) onChanged?.()
    } finally {
      setBusy("")
    }
  }

  const submit = async () => {
    setBusy("submit")
    setWarning("")
    try {
      const res = await ReviewProduct(pid, "submit", notes.trim())
      // what the company lacks ([{ key, label }], vendors eligibility): each by its name, in the reader's language
      if (res?.error === "company_not_ready") return fail(`${t`Your company details are not complete yet — the product is saved as a draft. Missing:`} ${(res.missing || []).map((item) => t(typeof item === "string" ? item : item?.label || item?.key || "")).filter(Boolean).join(", ")}.`)
      const err = errorMessage(res)
      if (err) return fail(res?.message || err)
      showNotification({ message: t("%s was sent for review", name.trim()), className: "bg-admin_success text-white" })
      onChanged?.()
      onClose()
    } finally {
      setBusy("")
    }
  }

  const labels = [t`Type`, t`Name`, t`Gallery`, t`Price`, t`Review`]
  const typeLabel = typeNode ? (productType ? nameOf(typeNode) : proposal?.name) : ""

  // the actions: in the dialog's footer, always in sight; the steps scroll between the title and them
  // (the buttons sit outside the form and submit it by its id)
  const footer = (
    <div className="flex flex-wrap items-center gap-2 w-full">
      {/* the product made: the full editor is a click away (everything the quick way leaves out) */}
      {created && (
        <Button type="button" variant="admin_text" onClick={() => onCreated(created)} data-testid="create-open-editor">
          {t`Open in the editor`}
        </Button>
      )}
      <span className="flex-1" />
      {step > 0 && (
        <Button type="button" variant="admin_secondary" onClick={() => (setWarning(""), setStep((current) => current - 1))} disabled={!!busy}>
          {t`Back`}
        </Button>
      )}
      {step < STEPS.length - 1 ? (
        <Button type="submit" form={FORM_ID} variant="admin_primary" disabled={!canNext || !!busy} data-testid="create-next">
          {busy === "next" ? t`Saving…` : step === 1 && !created ? t`Create and continue` : t`Next`}
        </Button>
      ) : (
        <Button type="submit" form={FORM_ID} variant="admin_primary" disabled={!!busy} data-testid="create-submit-review">
          {busy === "submit" ? t`Sending…` : t`Submit for review`}
        </Button>
      )}
    </div>
  )

  return (
    <Modal
      isOpen
      onClose={() => {
        // closed with a product made: the list behind follows it (a draft too)
        if (created) onChanged?.()
        onClose()
      }}
      variant="admin_compact"
      title={<span className="inline-flex items-center gap-1.5">{t`New product`}<HelpHint helpKey="vendor.products#new" /></span>}
      styleOverrides={{
        container: "bg-admin_paper w-full max-w-[760px] max-h-[calc(100vh-2rem)] overflow-hidden rounded-md border border-admin_bordered shadow-lg",
        content: "",
        footer: "px-4 py-3 border-t border-admin_bordered bg-admin_paper",
      }}
      footerContent={footer}
    >
      <form
        id={FORM_ID}
        className="flex flex-col gap-4 p-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (step === STEPS.length - 1) submit()
          else next()
        }}
        data-testid="quick-product"
      >
        {/* where in the steps */}
        <ol className="flex items-center gap-1 m-0 p-0 list-none" data-testid="quick-product-steps">
          {labels.map((label, index) => (
            // the last step takes only its own width: the line of steps runs edge to edge, centred
            <li key={label} className={`flex items-center gap-1 min-w-0 ${index < labels.length - 1 ? "flex-1" : "shrink-0"}`}>
              <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${index < step ? "bg-admin_success text-white" : index === step ? "bg-admin_primary text-white" : "bg-admin_neutral_1 text-admin_text_secondary"}`}>
                {index < step ? <CheckIcon className="w-4 h-4" /> : index + 1}
              </span>
              <span className={`hidden sm:block truncate text-xs ${index === step ? "font-semibold text-admin_text" : "text-admin_text_secondary"}`}>{label}</span>
              {index < labels.length - 1 && <span className="flex-1 h-px bg-admin_bordered mx-1" />}
            </li>
          ))}
        </ol>

        {STEPS[step] === "type" && (
          <>
            <Field label={t`Product type`}>
              <ProductTypeBrowser
                value={productType}
                proposal={proposal}
                t={t}
                onChange={({ slug: picked }) => {
                  setProductType(picked)
                  setProposal(null)
                  setWarning("")
                }}
                onPropose={(named) => {
                  setProposal(named)
                  setProductType("")
                  setWarning("")
                }}
              />
              {proposal && (
                <span className="text-sm" data-testid="product-type-proposal">
                  {t`Your type`}: <b>{proposal.name}</b>
                  <span className="ml-2 inline-flex items-center h-5 px-2 rounded-full bg-admin_warning/15 text-admin_warning text-[11px] font-semibold">{t`In review`}</span>
                </span>
              )}
            </Field>
          </>
        )}

        {STEPS[step] === "name" && (
          <>
            <Field label={t`Name`} hint={pid ? t("Product id: %s", pid) : t`The product's title, as the shop shows it`}>
              <TextInput
                value={name}
                onChange={(value) => {
                  setName(value)
                  setWarning("")
                }}
                placeholder={t`My new title`}
                autoFocus
                className={warning ? "border-admin_warning focus:border-admin_warning" : ""}
                data-testid="create-name"
              />
            </Field>
            <Field label={t`Description`} hint={t`What it is, what it is made of, who it is for. A blank line starts a new paragraph.`}>
              <TextArea value={description} onChange={setDescription} rows={7} maxLength={5000} data-testid="create-description" />
            </Field>
            <Field label={t`Tags`} hint={t`Words buyers may search your product by: a brand, a material, a flavour, an occasion. Press Enter to add.`}>
              <TagsEditor value={tags} onChange={setTags} productType={productType} t={t} />
            </Field>
          </>
        )}

        {STEPS[step] === "gallery" && (
          <>
            {/* the product editor's own pickers (MediaDropzone / MediaField: drop, paste or pick → crop → upload) */}
            <Field label={t`Main picture`} hint={t`Square — the shop's lists and the product page show it first`}>
              <div className="w-[220px]" data-testid="create-main-picture">
                <MediaDropzone
                  selected={urljoin(mediaRoot, mainImage)}
                  name={`${pid}-square`}
                  size="1024x1024"
                  aspect="1 / 1"
                  upload={(file) => UploadLocalizedImage(file, { ownerModel: "Product", owner: pid, field: "square", language: LANGUAGE })}
                  onUploaded={(data) => data?.url && setMainImage(data.url)}
                  onClear={() => setMainImage("")}
                  prompt={
                    <span className="flex flex-col items-center gap-1 text-xs text-admin_text_secondary">
                      <PhotoIcon className="w-8 h-8" />
                      {t`Add the main picture`}
                    </span>
                  }
                />
              </div>
            </Field>
            <Field label={t("More pictures (up to %s)", MAX_GALLERY)}>
              <GalleryField
                value={gallery}
                onChange={setGallery}
                max={MAX_GALLERY}
                t={t}
                src={(path) => urljoin(mediaRoot, path)}
                upload={(file) => UploadLocalizedImage(file, { ownerModel: "Product", owner: pid, language: LANGUAGE })}
                data-testid="create-gallery"
              />
            </Field>
            <Field label={t`Video (optional)`} hint={t`A YouTube link: it shows on the product page`}>
              <div className="relative">
                <PlayCircleIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-admin_text_secondary pointer-events-none" />
                <TextInput value={video} onChange={setVideo} placeholder="https://www.youtube.com/watch?v=…" className="pl-9" data-testid="create-video" />
              </div>
            </Field>
          </>
        )}

        {STEPS[step] === "price" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={<>{t`Price (EUR)`}<Required /></>} hint={t`What the buyer pays, VAT included`}>
              <TextInput value={price} onChange={setPrice} inputMode="decimal" placeholder="19.90" autoFocus data-testid="create-price" />
            </Field>
            {physical && (
              <Field label={<>{t`Weight (grams)`}<Required /></>} hint={t`With the packaging: the delivery price is computed from it`}>
                <TextInput value={weight} onChange={(value) => setWeight(value.replace(/[^\d]/g, ""))} inputMode="numeric" placeholder="500" data-testid="create-weight" />
              </Field>
            )}
            {physical && (
              <Field label={<>{t`In stock`}<Required /></>} hint={t`How many you have to sell: the store sells no more than that`}>
                <TextInput value={stock} onChange={(value) => setStock(value.replace(/[^\d]/g, ""))} inputMode="numeric" placeholder="10" data-testid="create-stock" />
              </Field>
            )}
          </div>
        )}

        {STEPS[step] === "review" && (
          <div className="flex flex-col gap-3">
            <div className="flex gap-4 rounded-md border border-admin_bordered p-3" data-testid="create-summary">
              {mainImage && <img src={urljoin(mediaRoot, mainImage)} alt="" className="w-24 h-24 rounded-md object-cover shrink-0" />}
              <div className="min-w-0 flex flex-col gap-1 text-sm">
                <b className="text-base">{name.trim()}</b>
                <span className="text-admin_text_secondary">
                  {typeLabel || proposal?.name}
                  {proposal && <span className="ml-2 inline-flex items-center h-5 px-2 rounded-full bg-admin_warning/15 text-admin_warning text-[11px] font-semibold" data-testid="create-type-in-review">{t`In review`}</span>}
                </span>
                {Number.isFinite(cents) && <span>€{(cents / 100).toFixed(2)}{physical && weight ? ` · ${weight} g` : ""}{physical && stock ? ` · ${t("%s in stock", stock)}` : ""}</span>}
                <span className="text-xs text-admin_text_secondary">{t("%s pictures", 1 + gallery.length)}{youtubeOf(video) ? ` · ${t`a video`}` : ""}</span>
                {!!tags.length && <span className="text-xs text-admin_text_secondary">{tags.join(" · ")}</span>}
              </div>
            </div>
            <Field label={t`Notes for the reviewer (optional)`}>
              <TextArea value={notes} onChange={setNotes} rows={3} maxLength={2000} data-testid="create-notes" />
            </Field>
          </div>
        )}

        {warning && (
          <div className="flex items-start gap-2 rounded-md border border-admin_warning/40 bg-admin_warning/10 px-3 py-2 text-sm text-admin_warning" role="alert" data-testid="create-warning">
            <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
            {/* the known refusals are ours to translate; a server's own words are shown as they came */}
            <span>{warning === COULD_NOT_CREATE || Object.values(REFUSED).includes(warning) ? t(warning) : warning}</span>
          </div>
        )}

      </form>
    </Modal>
  )
}
